import { useState } from 'react'
import { Button, MessageBar, Spinner } from '@fluentui/react-components'
import { apiFetchJson } from '../lib/apiFetch'
import { isXlsx, xlsxToCsv } from '../lib/xlsx'
import type { CsvDiffResponse } from '../types'

interface SyncResult {
  run: { recordsProcessed: number; errorsJson: string }
}

/**
 * CSV upload for 'csv'-ingestion modules — previews the diff against current data first
 * (POST .../csv/preview) and only applies it (POST .../csv) once the admin confirms, so a
 * wrong/partial export doesn't silently delete a pile of licenses.
 */
export default function CsvImportControl(props: { serviceId: string; onApplied: () => void }) {
  const { serviceId, onApplied } = props
  const [pending, setPending] = useState<{ fileName: string; csvText: string; diff: CsvDiffResponse } | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [applying, setApplying] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function handleFileSelect(file: File) {
    setPreviewing(true)
    setMessage(null)
    setError(null)
    try {
      // Vendor exports are often .xlsx (e.g. TeamFlect) — convert client-side to the CSV text the
      // preview/apply endpoints already expect, so nothing has to be saved as CSV by hand.
      const csvText = isXlsx(file) ? await xlsxToCsv(file) : await file.text()
      const diff = await apiFetchJson<CsvDiffResponse>(`/api/sync/${serviceId}/csv/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'text/csv' },
        body: csvText,
      })
      setPending({ fileName: file.name, csvText, diff })
    } catch (e) {
      setError(String(e))
    } finally {
      setPreviewing(false)
    }
  }

  async function handleConfirm() {
    if (!pending) return
    setApplying(true)
    setError(null)
    try {
      const res = await apiFetchJson<SyncResult>(`/api/sync/${serviceId}/csv`, {
        method: 'POST',
        headers: { 'Content-Type': 'text/csv' },
        body: pending.csvText,
      })
      const { recordsProcessed, errorsJson } = res.run
      const errorCount = (JSON.parse(errorsJson) as string[]).length
      setMessage(
        `Imported ${recordsProcessed} record${recordsProcessed === 1 ? '' : 's'}` +
          (errorCount ? `, ${errorCount} error${errorCount === 1 ? '' : 's'}` : '.'),
      )
      setPending(null)
      onApplied()
    } catch (e) {
      setError(String(e))
    } finally {
      setApplying(false)
    }
  }

  return (
    <div>
      {!pending ? (
        <input
          type="file"
          accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          disabled={previewing}
          onChange={e => {
            const file = e.target.files?.[0]
            if (file) handleFileSelect(file)
            e.target.value = ''
          }}
        />
      ) : (
        <div>
          <p style={{ marginTop: 0 }}>
            <strong>{pending.fileName}</strong>: {pending.diff.added.length} to add,{' '}
            {pending.diff.removed.length} to remove, {pending.diff.unchangedCount} unchanged.
          </p>
          {pending.diff.added.length > 0 && (
            <details style={{ marginBottom: 8 }}>
              <summary>Additions ({pending.diff.added.length})</summary>
              <ul>
                {pending.diff.added.map(a => (
                  <li key={`${a.personEmail}~${a.licenseType}`}>
                    {a.personEmail} — {a.licenseType}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {pending.diff.removed.length > 0 && (
            <details style={{ marginBottom: 8 }}>
              <summary>Removals ({pending.diff.removed.length})</summary>
              <ul>
                {pending.diff.removed.map(a => (
                  <li key={`${a.personEmail}~${a.licenseType}`}>
                    {a.personEmail} — {a.licenseType}
                  </li>
                ))}
              </ul>
            </details>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <Button appearance="primary" onClick={handleConfirm} disabled={applying}>
              {applying ? 'Applying...' : 'Confirm'}
            </Button>
            <Button onClick={() => setPending(null)} disabled={applying}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {previewing && <Spinner label="Comparing with current data..." style={{ marginTop: 8 }} />}
      {message && (
        <MessageBar intent="success" style={{ marginTop: 16 }}>
          {message}
        </MessageBar>
      )}
      {error && (
        <MessageBar intent="error" style={{ marginTop: 16 }}>
          Error: {error}
        </MessageBar>
      )}
    </div>
  )
}
